import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import * as colors from 'Styles/colors';
import * as Styled from './ContentCard.styled';
import CtaPropType from '../../prop-types/cta';
import RichTextPropTypes from '../../prop-types/rich-text';
import RichText from '../RichText/RichText';

import { ContentCardFragment } from './ContentCard.gql';
import ImageProps from '../../prop-types/image-props.pim';

/**
 * ContentCard component
 *
 * This component can be displayed in a grid or carousel
 * and will display:
 *
 *  - an image
 *  - a title
 *  - a subheader
 *  - one link or more links that will be displayed as buttons
 *
 */
const ContentCard = ({
  textAlignment,
  backgroundColor,
  customBackgroundColor,
  ctas,
  image,
  gridSpan,
  header,
  subheader,
  responsiveStyleSize,
  titleFontSize,
  containerBackgroundColor
}) => {
  const [areCtasRevealed, setAreCtasRevealed] = useState(false);
  const isTransparent = backgroundColor === 'transparent';
  const color = customBackgroundColor || colors[backgroundColor || 'white'];
  const backgroundHexcode =
    (!isTransparent && color) || containerBackgroundColor || colors.white;

  const src = image?.file.url;
  const height = image?.file && image.file.details.image.height;
  const width = image?.file && image.file.details.image.width;

  const handleGlobalClick = () => setAreCtasRevealed(false);

  useEffect(() => {
    if (areCtasRevealed) {
      document.addEventListener('click', handleGlobalClick, {
        capture: true,
        once: true
      });
    }
  }, [areCtasRevealed]);

  const cardImage = (
    <Styled.Image
      background={backgroundHexcode}
      crop={{
        height,
        width
      }}
      cropType="fit"
      $isTransparent={isTransparent}
      resolution={{
        small: 270,
        medium: 540,
        large: 720
      }}
      src={src.match(/https|http/gi) ? src : `https://${src}`}
      alt={image.description || ''}
      onClick={() => ctas?.length > 1 && setAreCtasRevealed(true)}
      $isClickable={ctas?.length > 0}
      $areCtasRevealed={areCtasRevealed}
    />
  );

  return (
    <Styled.ContentCard
      $backgroundColor={color}
      $gridSpan={gridSpan}
      $areCtasRevealed={areCtasRevealed}
    >
      {ctas?.length === 1 ? (
        <Styled.Link
          href={ctas[0].url}
          aria-label={image.description || header || 'Bombas'}
        >
          {cardImage}
        </Styled.Link>
      ) : (
        cardImage
      )}
      <Styled.Copy
        $backgroundColor={backgroundHexcode}
        $alignment={textAlignment}
        $gridSpan={gridSpan}
        $withPadding={!isTransparent}
        $responsiveStyleSize={responsiveStyleSize}
        $areCtasRevealed={areCtasRevealed}
      >
        {!!header && (
          <Styled.Title
            $backgroundColor={backgroundHexcode}
            $responsiveStyleSize={responsiveStyleSize}
            $titleFontSize={titleFontSize}
          >
            {ctas?.length === 1 ? (
              <Styled.Link href={ctas[0].url}>{header}</Styled.Link>
            ) : (
              header
            )}
          </Styled.Title>
        )}
        {!!subheader && !!Object.keys(subheader).length && (
          <Styled.Subheader $responsiveStyleSize={responsiveStyleSize}>
            <RichText richText={subheader} />
          </Styled.Subheader>
        )}
        {ctas?.length === 1 && !!(ctas[0].text && ctas[0].text.trim()) && (
          <Styled.Cta
            as="a"
            href={ctas[0].url}
            size={responsiveStyleSize === 'mega' ? 'medium' : 'small'}
            variant="defaultDark"
            $backgroundColor={backgroundHexcode}
          >
            {ctas[0].text}
          </Styled.Cta>
        )}
        {ctas?.length > 1 && ctas[0].text && ctas[0].text.trim() && (
          <Styled.Cta
            $backgroundColor={backgroundHexcode}
            key={header + ctas[0].text + ctas[0].url}
            onClick={() => setAreCtasRevealed(true)}
            role="button"
            size={responsiveStyleSize === 'mega' ? 'medium' : 'small'}
            variant="defaultDark"
          >
            {ctas[0].text}
          </Styled.Cta>
        )}
      </Styled.Copy>
      {ctas?.length > 1 && areCtasRevealed && (
        <Styled.CtasContainer>
          <Styled.CtasBackdrop $areCtasRevealed={areCtasRevealed} />
          <Styled.Ctas $responsiveStyleSize={responsiveStyleSize}>
            {ctas.slice(1).map(
              ({ text, url }) =>
                text &&
                text.trim() && (
                  <Styled.Cta
                    as="a"
                    href={url}
                    key={header + text + url}
                    size={responsiveStyleSize === 'mega' ? 'large' : 'medium'}
                    $backgroundColor={backgroundHexcode}
                  >
                    {text}
                  </Styled.Cta>
                )
            )}
          </Styled.Ctas>
        </Styled.CtasContainer>
      )}
    </Styled.ContentCard>
  );
};

ContentCard.fragment = ContentCardFragment;

ContentCard.propTypes = {
  /**
   * Alignment of text
   */
  textAlignment: PropTypes.oneOf(['LEFT', 'CENTER']),
  /**
   * Background color
   */
  backgroundColor: PropTypes.oneOf(['transparent', ...Object.keys(colors)]),
  /**
   * A hex code.
   *
   * Overrides backgroundColor if truthy
   */
  customBackgroundColor: PropTypes.string,
  /**
   * Links to display
   */
  ctas: PropTypes.arrayOf(PropTypes.shape(CtaPropType)),
  /**
   * An image object
   */
  image: ImageProps,
  /**
   * Column spans in a 12 column grid
   *
   * Accepts a number or a string
   */
  gridSpan: ({ gridSpan }, propName, componentName) =>
    gridSpan && gridSpan >= 1 && gridSpan <= 12
      ? null
      : new Error(`${propName} in ${componentName} is not between 1 and 12`),
  /**
   * Title to display
   */
  header: PropTypes.string,
  /**
   * The supporting text for the header.
   *
   * Renders as contentful rich text
   */
  subheader: PropTypes.shape(RichTextPropTypes),
  /**
   * Applies styling to copy based on content system responsive rules if titleFontSize is not passed in
   */
  responsiveStyleSize: PropTypes.oneOf(['mini', 'regular', 'mega']),
  /**
   * Changes the size of the header text underneath the Content Card
   */
  titleFontSize: PropTypes.number,
  /**
   * Background color of the parent container
   */
  containerBackgroundColor: PropTypes.string
};

ContentCard.defaultProps = {
  textAlignment: 'LEFT',
  backgroundColor: 'white',
  customBackgroundColor: null,
  ctas: [],
  image: {},
  gridSpan: 6,
  header: '',
  subheader: {},
  responsiveStyleSize: 'regular',
  titleFontSize: undefined,
  containerBackgroundColor: colors.white
};

export default ContentCard;
